// Existing Cloudflare SFU protocol and track operations. The app still owns its mic, peer/UI state, hearing policy
// and serial queue; callbacks bridge the current page-specific state during this command-style migration.
// This is a transitional SFU adapter, not yet a common interface for other RTC providers.
export function createSfuAdapter(o) {
  const { local, stale, gone, serial, send } = o;
  const backoff = (...args) => o.backoff(...args);
  const backedOff = (...args) => o.backedOff(...args);
  // the office checks every SFU call (office/sfu.ts): pulling someone's track only while it says I hear them
  // &server=: the server this page is on (a member's page): the call goes to that server's office or not at all (409
  // E_SERVER: this browser's active server changed under the page, routes/sfu.ts), never to another server's office,
  // where my session would be one nobody here can hear and every pull a 403
  async function sfu(path, method = 'POST', body, options = {}) {
    const { room, server } = options.context ?? { room: o.room(), server: o.server() };
    const srv = server ? '&server=' + encodeURIComponent(server) : '';
    const r = await fetch(o.vq('/sfu/' + path + '?room=' + encodeURIComponent(room) + srv), { method, headers: { 'content-type': 'application/json' }, body: body && JSON.stringify(body), ...(options.signal ? { signal: options.signal } : {}) });
    const j = await r.json();
    const current = !options.valid || options.valid();
    if (!options.quiet && current && r.status === 409 && j.errorCode === 'E_SERVER') o.serverMoved();
    const session = /^sessions\/([^/]+)\//.exec(path)?.[1];
    if (!options.quiet && current && j.errorCode === 'E_SFU_REBUILD' && session && session === local.sessionId) o.sessionReset?.(session);
    if (!r.ok || j.errorCode) throw Object.assign(new Error(path + ': ' + (j.errorDescription || r.status)), { status: r.status, code: j.errorCode }); return j;
  }
  // STUN, plus Cloudflare TURN (UDP/TCP/TLS 443) when the worker has a key (routes/sfu.ts): networks that block direct
  // UDP to the SFU only connect through the relay. Kept until 5 min before its TTL; any failure = STUN only, not cached.
  const STUN_ONLY = [{ urls: 'stun:stun.cloudflare.com:3478' }];
  let iceCache = null; // { servers, until }
  async function iceConfig(signal) {
    if (iceCache && Date.now() < iceCache.until) return iceCache.servers;
    try {
      const r = await fetch(o.vq('/sfu/ice'), { cache: 'no-store', ...(signal ? { signal } : {}) }); if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json(); if (!Array.isArray(j.iceServers) || !j.iceServers.length) throw new Error('no iceServers');
      iceCache = { servers: j.iceServers, until: Date.now() + Math.max(0, (Number(j.ttl) || 0) - 300) * 1000 };
      return j.iceServers;
    } catch (e) { console.warn('ice servers: STUN only:', e.message); return STUN_ONLY; }
  }

  let starting = null;
  // An abort is only local: the provider may still finish the older request. The Office retains its uncertainty.
  function closeStarting(p) {
    if (!p.dispatched || !p.sid || !p.mid) return;
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 5000);
    sfu(`sessions/${p.sid}/tracks/close`, 'PUT', { tracks: [{ mid: p.mid }], force: true },
      { context: p.context, signal: controller.signal, quiet: true }).catch(() => {}).finally(() => clearTimeout(timer));
  }
  function cancelPublish() {
    const p = starting; if (!p) return;
    starting = null; p.cancelled = true; p.controller.abort(); closeStarting(p);
  }
  /** My connection to the SFU: publish the mic, then announce its session to the Office. */
  async function publishMic() {
    if (starting) throw new Error('voice start already pending');
    const p = { gen: local.gen, context: { room: o.room(), server: o.server() }, controller: new AbortController(), cancelled: false,
      pc: null, sid: null, mid: null, dispatched: false };
    starting = p;
    const valid = () => starting === p && !p.cancelled && !stale(p.gen) && !o.voiceStopped() && (!p.pc || local.pc === p.pc);
    const opts = { context: p.context, signal: p.controller.signal, valid };
    try {
      const iceServers = await iceConfig(p.controller.signal);
      if (!valid()) return false;
      const pc = (p.pc = local.pc = new RTCPeerConnection({ iceServers, bundlePolicy: 'max-bundle' }));
      pc.ontrack = o.onTrack;
      o.watchPc(pc);
      const s = await sfu('sessions/new', 'POST', undefined, opts);
      p.sid = s.sessionId;
      if (!valid()) return false;
      local.sessionId = p.sid;
      const tx = pc.addTransceiver(local.send.track, { direction: 'sendonly' });
      const offer = await pc.createOffer(); if (!valid()) return false;
      await pc.setLocalDescription(offer); if (!valid()) return false;
      p.mid = tx.mid;
      if (!p.mid) throw new Error('microphone has no mid');
      const name = 'mic-' + crypto.randomUUID().slice(0, 8); local.trackName = name;
      p.dispatched = true;
      const r = await sfu(`sessions/${p.sid}/tracks/new`, 'POST', { sessionDescription: { type: 'offer', sdp: pc.localDescription.sdp }, tracks: [{ location: 'local', mid: p.mid, trackName: name }] }, opts);
      if (!valid()) { closeStarting(p); return false; }
      await pc.setRemoteDescription(r.sessionDescription);
      if (!valid()) { closeStarting(p); return false; }
      send({ t: 'track', sessionId: p.sid, trackName: name });
      local.sender = tx.sender;
      // Wait for ICE/DTLS before the first pulls, or retry after the existing short connection grace.
      await connectedOr(pc, 5000);
      if (!valid()) return false;
      if (['failed', 'closed'].includes(pc.connectionState)) throw Object.assign(new Error('voice connection failed'), { code: 'E_SFU_REBUILD' });
      return true;
    } catch (error) {
      if (!valid()) return false;
      closeStarting(p);
      throw error;
    } finally { if (starting === p) starting = null; }
  }

  /** `pc` is connected (or failed, closed, or `ms` passed: pull() tries again on a refusal anyway). */
  function connectedOr(pc, ms) {
    const up = () => pc.connectionState === 'connected' || ['connected', 'completed'].includes(pc.iceConnectionState);
    if (up() || ['failed', 'closed'].includes(pc.connectionState)) return Promise.resolve();
    return new Promise((ok) => {
      const done = () => { clearTimeout(t); pc.removeEventListener('connectionstatechange', see); pc.removeEventListener('iceconnectionstatechange', see); ok(); };
      const see = () => { if (up() || ['failed', 'closed'].includes(pc.connectionState)) done(); };
      const t = setTimeout(done, ms);
      pc.addEventListener('connectionstatechange', see); pc.addEventListener('iceconnectionstatechange', see);
    });
  }

  async function renegotiate(r, g = local.gen) {
    if (!r.requiresImmediateRenegotiation || !r.sessionDescription) return;
    const pc = local.pc, sid = local.sessionId;
    if (stale(g) || !pc) throw gone();
    await pc.setRemoteDescription(r.sessionDescription); if (stale(g)) throw gone();
    const answer = await pc.createAnswer(); if (stale(g)) throw gone();
    await pc.setLocalDescription(answer); if (stale(g)) throw gone();
    await sfu(`sessions/${sid}/renegotiate`, 'PUT', { sessionDescription: { type: 'answer', sdp: pc.localDescription.sdp } });
  }
  /** Stop sending or receiving these mids on my SFU session. Cloudflare closes tracks only with a new offer that no longer
   *  has them (406 "sessionDescription must be present when closing tracks" otherwise): the transceivers stop here, the
   *  offer goes with the close, and its answer comes back. If anything fails, a half-done offer is rolled back, so the next
   *  pull's renegotiation still works. */
  async function closeTracks(mids, g = local.gen) {
    const pc = local.pc, sid = local.sessionId; if (stale(g) || !pc || !sid || pc.connectionState === 'closed' || !mids.length) return;
    const rollback = async () => { if (pc.signalingState !== 'stable' && pc.signalingState !== 'closed') { try { await pc.setLocalDescription({ type: 'rollback' }); } catch {} } };
    await rollback(); // an earlier renegotiation that failed halfway: start from a stable state
    for (const t of pc.getTransceivers()) if (mids.includes(t.mid)) { try { t.stop(); } catch {} }
    try {
      await pc.setLocalDescription(await pc.createOffer());
      const r = await sfu(`sessions/${sid}/tracks/close`, 'PUT', { tracks: mids.map((mid) => ({ mid })), sessionDescription: { type: 'offer', sdp: pc.localDescription.sdp }, force: false });
      if (stale(g)) return; // (that connection is closed: nothing left to answer)
      if (r.sessionDescription?.type === 'answer') await pc.setRemoteDescription(r.sessionDescription);
      else await renegotiate(r, g);
    } finally { await rollback(); } // no answer came (an error, or an odd reply): not left half-negotiated
  }
  /** Someone's video, pulled from the SFU: k = 'screen' (p.screenTrack → p.screenMid) or 'cam' (p.camTrack → p.camMid). */
  async function pullVideo(p, k) {
    const g = local.gen;
    try {
      if (!local.ready) return; // (busy cleared below: tried again once voice is up)
      if (p[k + 'Mid']) await unpullVideo(p, k, true);
      if (stale(g)) return;
      const name = p[k + 'Track'];
      let r;
      try { r = await sfu(`sessions/${local.sessionId}/tracks/new`, 'POST', { tracks: [{ location: 'remote', sessionId: p.sessionId, trackName: name }] }); }
      catch (e) { if (!stale(g)) backoff(p, k, e.status === 403); throw e; }
      if (stale(g)) return; // (pulled on the connection that was lost: voiceLost let go of everything on it)
      const t = refused(r);
      if (t) { // not marked pulled: syncAudio asks again after the wait
        backoff(p, k);
        if (r.requiresImmediateRenegotiation) await renegotiate(r, g).catch((e) => console.error(e)); // (keep the session in step, as pull)
        throw new Error(`pull ${name}: ${t}`);
      }
      p[k + 'Mid'] = r.tracks[0].mid; p[k + 'Pulled'] = name;
      try { await renegotiate(r, g); } catch (e) { if (stale(g)) return; await unpullVideo(p, k, true).catch(() => {}); backoff(p, k); throw e; }
      backedOff(p, k);
    } finally { if (!stale(g)) p[k + 'Busy'] = false; } // (else voiceLost cleared it, and a new pull may hold it now)
  }
  async function unpullVideo(p, k, keepBusy) {
    const g = local.gen;
    try {
      const mid = p[k + 'Mid']; p[k + 'Mid'] = null; p[k + 'Pulled'] = null;
      if (k === 'screen') o.hideScreen(p); else { p.camStream = null; o.refreshMeeting(); }
      if (mid && local.pc) await closeTracks([mid], g);
    } finally { if (!keepBusy && !stale(g)) p[k + 'Busy'] = false; }
  }

  // A screen capture belongs to one visit to one room/call and one SFU connection. The caller supplies its scope;
  // the picker and each negotiation boundary recheck it, so another space or a rejoin cannot inherit the capture.
  function screenValid(p) {
    return !!p && !p.cancelled && !o.voiceStopped() && local.ready && local.gen === p.g && local.pc === p.pc &&
      local.sessionId === p.sid && !['closed', 'failed'].includes(p.pc.connectionState) &&
      o.shareScope() === p.scope && (!p.track || p.track.readyState === 'live');
  }
  function forceCloseScreen(p) {
    if (!p.attempted || !p.sid || !p.mid) return;
    // The server may have accepted the request even if the response failed or arrived after cancellation. Close the
    // captured session/mid, never a newer connection's, and try again after the pending request settles.
    sfu(`sessions/${p.sid}/tracks/close`, 'PUT', { tracks: [{ mid: p.mid }], force: true }).catch(() => {});
  }
  function cancelScreenRequest(p) {
    if (!p || p.cancelled) return;
    p.cancelled = true;
    if (o.share().pending === p) o.share().pending = null;
    p.stream?.getTracks().forEach((track) => track.stop());
    if (p.tx) { try { p.pc.removeTrack(p.tx.sender); } catch {} try { p.tx.stop(); } catch {} }
    forceCloseScreen(p);
    // Some WebRTC promises never settle after interruption. Do not leave the SFU queue blocked forever: once capture
    // is already off, replace only this still-current connection if its cancelled operation has not unwound.
    if (p.working) p.recoveryTimer = setTimeout(() => {
      if (!p.finished && !o.voiceStopped() && local.ready && local.gen === p.g && local.pc === p.pc) { forceCloseScreen(p); o.screenStalled(); }
    }, 5000);
  }
  async function cleanupScreenRequest(p) {
    cancelScreenRequest(p);
    // This old peer connection remains owned by the queue until the operation settles; a rejoin uses another one.
    if (p.pc.signalingState === 'have-local-offer') { try { await p.pc.setLocalDescription({ type: 'rollback' }); } catch {} }
    forceCloseScreen(p);
  }
  async function publishScreen(p) {
    const valid = () => { if (o.share().pending === p && screenValid(p)) return true; cancelScreenRequest(p); return false; };
    let committed = false;
    p.working = true;
    try {
      if (!valid()) return;
      p.tx = p.pc.addTransceiver(p.track, { direction: 'sendonly' });
      const offer = await p.pc.createOffer(); if (!valid()) return;
      await p.pc.setLocalDescription(offer); if (!valid()) return;
      if (!p.tx.mid) throw new Error('screen share has no mid');
      p.mid = p.tx.mid;
      const name = 'screen-' + crypto.randomUUID().slice(0, 8);
      p.attempted = true;
      const r = await sfu(`sessions/${p.sid}/tracks/new`, 'POST', { sessionDescription: { type: 'offer', sdp: p.pc.localDescription.sdp }, tracks: [{ location: 'local', mid: p.mid, trackName: name }] });
      if (!valid()) return;
      await p.pc.setRemoteDescription(r.sessionDescription); if (!valid()) return;
      Object.assign(o.share(), { mid: p.mid, stream: p.stream, transceiver: p.tx, name, active: p, pending: null });
      committed = true;
      send({ t: 'screen', trackName: name });
      o.screenStarted();
    } catch (e) {
      if (committed && o.share().active === p) stopScreen();
      if (!p.cancelled) console.error('screen share:', e);
    } finally {
      if (!committed) await cleanupScreenRequest(p);
      if (o.share().pending === p) o.share().pending = null;
      p.finished = true; clearTimeout(p.recoveryTimer);
    }
  }
  function enforceScreenScope() {
    const share = o.share();
    if (share.pending && !screenValid(share.pending)) cancelScreenRequest(share.pending);
    if (share.active && !screenValid(share.active)) stopScreen();
  }
  function startScreen(scope) {
    const share = o.share();
    if (share.mid || share.pending) return stopScreen();
    if (scope == null || !local.ready || !local.pc || !local.sessionId) return;
    const p = { scope, g: local.gen, pc: local.pc, sid: local.sessionId, cancelled: false, attempted: false, stream: null, track: null, tx: null };
    share.pending = p;
    // Directly in the click gesture and outside the SFU queue: an open picker must not block audio pulls.
    let capture;
    try { capture = navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 15 }, audio: false }); }
    catch (e) { cancelScreenRequest(p); console.error('screen share:', e); return; }
    Promise.resolve(capture).then((stream) => {
      p.stream = stream; p.track = stream.getVideoTracks()[0];
      if (!p.track || share.pending !== p || !screenValid(p)) { cancelScreenRequest(p); stream.getTracks().forEach((track) => track.stop()); return; }
      p.track.onended = () => { if (share.pending === p || share.active === p) stopScreen(); };
      serial(() => publishScreen(p));
    }).catch((e) => { cancelScreenRequest(p); if (e?.name !== 'NotAllowedError') console.error('screen share:', e); });
  }
  function stopScreen() {
    const share = o.share();
    cancelScreenRequest(share.pending);
    const p = share.active; if (!p) return;
    p.cancelled = true;
    Object.assign(share, { mid: null, name: null, stream: null, transceiver: null, active: null });
    p.stream?.getTracks().forEach((track) => track.stop());
    try { send({ t: 'screen', trackName: null }); } catch (e) { console.error(e); }
    try { o.screenStopped(); } catch (e) { console.error(e); }
    serial(async () => {
      let finished = false;
      const timer = setTimeout(() => {
        if (!finished && !o.voiceStopped() && local.ready && local.gen === p.g && local.pc === p.pc) { forceCloseScreen(p); o.screenStalled(); }
      }, 5000);
      try {
        if (!stale(p.g) && local.pc === p.pc && local.sessionId === p.sid && p.pc.connectionState !== 'closed') {
          await closeTracks([p.mid], p.g); return;
        }
      } catch (e) { console.error(e); }
      finally { finished = true; clearTimeout(timer); }
      try { p.tx.stop(); } catch {} forceCloseScreen(p);
    });
  }

  async function unpull(p) {
    const mid = p.mid, g = local.gen; if (!mid || !local.pc) { p.closing = false; return; }
    try {
      await closeTracks([mid], g);
    } finally { // even if the close failed (the office may have closed it already): it's gone from here
      // (unless voice reconnected meanwhile: voiceLost let go of it, and p may hold a pull on the new connection now)
      if (!stale(g)) { p.gainNode?.disconnect(); p.gainNode = null; p.level = null; p.mid = null; p.pulling = false; p.closing = false; }
    }
  }
  /** Why the SFU gave no track for a pull that went through (200): Cloudflare answers a track it can't give with an
   *  errorCode on that track, not on the call (e.g. pulled into a session not connected yet); null when it's there. */
  function refused(r) {
    const t = r?.tracks?.[0];
    return !t ? 'no track in the answer' : t.errorCode ? `${t.errorCode} ${t.errorDescription ?? ''}`.trim() : typeof t.mid !== 'string' || !t.mid ? 'no mid' : null;
  }
  async function pull(p) {
    // my voice isn't up yet (the welcome brings everyone's tracks seconds before it is): not pulling, so syncAudio
    // tries again once it is (audioStart), instead of never
    if (!local.ready) { p.pulling = false; return; }
    // Anything short of a pulled track leaves p not pulling, with a wait (backoff): syncAudio pulls again while I still
    // hear them. (A refusal that left p.pulling set, as a per-track error or a failed renegotiation did, meant never:
    // P0 2026-11-03.)
    // Voice reconnected while this was under way (gen, voiceLost): it was on the old connection, which let go of
    // everything; p is left as it is now (a pull on the new one may be under way).
    const g = local.gen, again = (flat) => { if (!stale(g)) { p.pulling = false; backoff(p, 'pull', flat); } };
    let r;
    // refused (403: the office says I don't hear them, e.g. they just walked off, or not yet: a flat short wait)
    try { r = await sfu(`sessions/${local.sessionId}/tracks/new`, 'POST', { tracks: [{ location: 'remote', sessionId: p.sessionId, trackName: p.trackName }] }); }
    catch (e) { again(e.status === 403); throw e; }
    if (stale(g)) return;
    const no = refused(r);
    if (no) {
      again(); console.warn('pull', p.trackName, 'refused by the SFU:', no);
      if (r.requiresImmediateRenegotiation) await renegotiate(r, g).catch((e) => console.error(e)); // (keep the session in step)
      return;
    }
    p.mid = r.tracks[0].mid;
    try { await renegotiate(r, g); }
    catch (e) { // the track never came: let go of it, and pull again
      if (stale(g)) return;
      const mid = p.mid; p.mid = null;
      await closeTracks([mid], g).catch(() => {});
      again(); throw e;
    }
    if (!stale(g)) backedOff(p, 'pull');
  }

  /** Publish a captured camera. Any failure undoes everything: the capture stops (the light goes off), the
   *  transceiver goes, a half-done offer is rolled back, and a track the SFU already took is closed there. */
  async function publishCam(stream, c) {
    const pc = local.pc, sid = local.sessionId, g = local.gen;
    const live = () => !o.voiceStopped() && !stale(g) && pc === local.pc && pc?.connectionState !== 'closed';
    let tx = null, published = false; // the camera's transceiver (not t: that's the page's words)
    try {
      if (!c.inRoom() || !pc || !sid || !live()) throw 'gone'; // walked out (or voice dropped) meanwhile
      const track = stream.getVideoTracks()[0]; track.contentHint = 'motion';
      tx = pc.addTransceiver(track, { direction: 'sendonly', sendEncodings: [{ maxBitrate: c.maxBitrate, maxFramerate: 15 }] });
      await pc.setLocalDescription(await pc.createOffer());
      if (!live()) throw 'gone';
      const name = 'cam-' + crypto.randomUUID().slice(0, 8);
      const r = await sfu(`sessions/${sid}/tracks/new`, 'POST', { sessionDescription: { type: 'offer', sdp: pc.localDescription.sdp }, tracks: [{ location: 'local', mid: tx.mid, trackName: name }] });
      published = true;
      if (!live()) throw 'gone';
      await pc.setRemoteDescription(r.sessionDescription);
      if (!live()) throw 'gone';
      Object.assign(c.cam, { on: true, mid: tx.mid, stream, name, transceiver: tx });
      track.onended = () => serial(c.stop); // unplugged, or revoked in the browser
      send({ t: 'cam', trackName: name });
      if (!c.inRoom()) await c.stop();
    } catch (e) {
      stream.getTracks().forEach((x) => x.stop());
      if (pc && pc.signalingState === 'have-local-offer') { try { await pc.setLocalDescription({ type: 'rollback' }); } catch {} }
      if (tx) { try { pc.removeTrack(tx.sender); } catch {} try { tx.stop(); } catch {} }
      if (published && tx?.mid) sfu(`sessions/${sid}/tracks/close`, 'PUT', { tracks: [{ mid: tx.mid }], force: true }).catch(() => {});
      if (e !== 'gone') { console.error('camera', e); c.onError(); }
    } finally { c.cam.busy = false; c.render(); }
  }

  async function unpublishCam(mid, transceiver) {
    const pc = local.pc;
    if (mid && pc && pc.connectionState !== 'closed') {
      try { await closeTracks([mid]); } catch (e) { console.error(e); }
    }
    try { transceiver?.stop(); } catch {}
  }

  return { publishMic, cancelPublish, startScreen, stopScreen, enforceScreenScope, publishCam, unpublishCam,
    pull, unpull, pullVideo, unpullVideo, closeTracks, serial, ready: () => !!local.ready };
}
